import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, SlidersHorizontal, CheckCircle2, AlertTriangle, Lock } from "lucide-react";

function FormatSelect({ testid, label, value, catalog, onChange }) {
  return (
    <label className="block text-xs text-slate-400">
      {label}
      <Select data-testid={`${testid}-root`} value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1 bg-[#0d1016]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {(catalog || []).map((c) => (
            <SelectItem key={c.format} data-testid={`${testid}-opt-${c.format}`} value={c.format} disabled={!c.available}>
              {c.label}{!c.available ? ` — needs ${c.requires_tier}+` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

export function AudioOutputPanel({ projectId }) {
  const [d, setD] = useState(null);
  const [audition, setAudition] = useState("");
  const [pref, setPref] = useState("");
  const [fb, setFb] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/audio-output`);
    setD(data); setAudition(data.audition); setPref(data.production_preferred); setFb(data.production_fallback);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/audio-output`, {
        audition, production_preferred: pref, production_fallback: fb });
      setD(data); toast.success("Audio output profiles saved (affects only future generations).");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="mb-8 flex justify-center rounded-xl border border-border bg-[#12161f] py-10"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>;
  const dirty = audition !== d.audition || pref !== d.production_preferred || fb !== d.production_fallback;

  return (
    <div className="mb-8 rounded-xl border border-border bg-[#12161f] p-6" data-testid="audio-output-panel">
      <div className="mb-1 flex items-center gap-2">
        <SlidersHorizontal className="h-5 w-5 text-amber-400" />
        <h2 className="font-serif text-2xl text-slate-100">Audio Output</h2>
      </div>
      <p className="mb-4 text-sm text-slate-400">
        ElevenLabs source-generation formats. {d.connected
          ? <>Connected tier: <span className="font-mono text-amber-300">{d.tier || "unknown"}</span> — unavailable formats are disabled.</>
          : <span className="text-amber-300">Not connected — connect ElevenLabs in Settings to unlock higher-tier formats.</span>}
        {" "}Separate from Master Assembly export settings.
      </p>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-[#0d1016] p-4">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Audition profile <span className="text-slate-600">· temporary tests</span></div>
          <FormatSelect data-testid="audio-audition-select-field" testid="audio-audition-select" label="Format" value={audition} catalog={d.catalog} onChange={setAudition} />
          <div className="mt-2 text-[11px] text-slate-500">Used by pronunciation auditions & quick checks. Resolved: <span className="font-mono text-slate-300">{d.audition_label}</span></div>
        </div>
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.04] p-4">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Production-take profile <span className="text-slate-600">· passages + repairs</span></div>
          <div className="space-y-2">
            <FormatSelect data-testid="audio-prod-pref-select-field" testid="audio-prod-pref-select" label="Preferred" value={pref} catalog={d.catalog} onChange={setPref} />
            <FormatSelect data-testid="audio-prod-fb-select-field" testid="audio-prod-fb-select" label="Fallback (if tier can't use preferred)" value={fb} catalog={d.catalog} onChange={setFb} />
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px]" data-testid="audio-prod-resolved">
            {d.production_resolved_lossy
              ? <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
              : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
            <span className="text-slate-400">Resolved now: <span className="font-mono text-slate-200">{d.production_resolved_label}</span> ({d.production_resolved_source}){d.production_resolved_lossy ? " · lossy" : " · lossless"}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button data-testid="audio-output-save" onClick={save} disabled={!dirty || busy} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save profiles
        </Button>
        <span className="flex items-center gap-1 text-[11px] text-slate-500"><Lock className="h-3 w-3" /> Existing takes keep their original format; changes apply to future generations only.</span>
      </div>
    </div>
  );
}
